import { useState } from "react";
import { Frame } from "@/components/diagrams/kit";
import { Slider } from "@/components/ui/slider";

const PARTS = [
  ["instructions", "System instructions", 1200, "bg-trust"],
  ["tools", "Tool definitions", 1500, "bg-brand-data"],
  ["history", "Conversation history", 2500, "bg-brand-data/60"],
  ["docs", "Retrieved documents", 4000, "bg-brand-accent"],
  ["live", "Live query results", 2000, "bg-brand-primary"],
  ["question", "User question", 150, "bg-foreground/60"],
  ["output", "Output reserve", 2000, "bg-brand-action"],
  ["safety", "Safety margin", 1000, "bg-muted-foreground/50"],
];
const LIMITS = [8000, 32000, 128000];

export default function ContextBudget() {
  const [v, setV] = useState(Object.fromEntries(PARTS.map(([k, , d]) => [k, d])));
  const [limit, setLimit] = useState(32000);
  const [dumped, setDumped] = useState(false);
  const total = Object.values(v).reduce((a, b) => a + b, 0) + (dumped ? 60000 : 0);
  const over = total > limit;
  const lowReserve = v.output < 0.05 * limit;
  const scale = Math.max(total, limit);
  return (
    <Frame title="Context window budget" hint="Drag the sliders" testId="diagram-context-budget">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">Model context limit:</span>
        {LIMITS.map((l) => (
          <button key={l} type="button" data-testid={`budget-limit-${l}`} onClick={() => setLimit(l)} className={`rounded-sm border px-2.5 py-1 font-mono text-xs ${limit === l ? "border-brand-primary bg-brand-primary text-brand-primary-fg" : "border-border"}`}>{l / 1000}k</button>
        ))}
      </div>
      <div className="relative mt-4 flex h-9 w-full overflow-hidden border border-border bg-muted" data-testid="budget-bar">
        {PARTS.map(([k, , , c]) => <div key={k} title={k} className={`${c} h-full transition-[width] duration-300`} style={{ width: `${(v[k] / scale) * 100}%` }} />)}
        {dumped && <div className="h-full bg-destructive/70 transition-[width]" style={{ width: `${(60000 / scale) * 100}%` }} title="Raw logs" />}
        <div className="absolute top-0 h-full w-0.5 bg-foreground" style={{ left: `${(limit / scale) * 100}%` }} />
      </div>
      <div className="mt-2 flex justify-between font-mono text-xs">
        <span data-testid="budget-total">{total.toLocaleString()} / {limit.toLocaleString()} tokens</span>
        <span data-testid="budget-status" className={over ? "font-bold text-destructive" : lowReserve ? "font-bold text-brand-action" : "text-brand-accent"}>
          {over ? "Over the limit — request fails or truncates" : lowReserve ? "Output reserve too small — answer may be cut off" : "Fits, with output reserve and safety margin"}
        </span>
      </div>
      <div className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {PARTS.map(([k, label, , c]) => (
          <div key={k}>
            <div className="flex justify-between text-xs"><span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 ${c}`} />{label}</span><span className="font-mono">{v[k].toLocaleString()}</span></div>
            <Slider data-testid={`budget-slider-${k}`} className="mt-2" min={0} max={12000} step={50} value={[v[k]]} onValueChange={([n]) => setV({ ...v, [k]: n })} aria-label={label} />
          </div>
        ))}
      </div>
      <button type="button" data-testid="budget-dump-logs" onClick={() => setDumped(!dumped)} className={`mt-6 w-full border p-3 text-left text-sm ${dumped ? "border-destructive bg-destructive/10" : "border-dashed border-border hover:bg-muted"}`}>
        <strong>{dumped ? "Undo: remove 30 days of raw alarm logs" : "Anti-pattern: dump 30 days of raw alarm logs (+60k)"}</strong>
        <span className="block text-xs text-muted-foreground">“Token maxing” is a design failure. Use a fit-for-purpose context pack instead.</span>
      </button>
    </Frame>
  );
}
